function NumberFormatter(num, digits) {
    const lookup = [
      { value: 1, symbol: "" },
      { value: 1e3, symbol: "k" },
      { value: 1e6, symbol: "M" },
      { value: 1e9, symbol: "G" },
      { value: 1e12, symbol: "T" },
      { value: 1e15, symbol: "P" },
      { value: 1e18, symbol: "E" }
    ];
    const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    var item = lookup.slice().reverse().find(function(item) {
      return num >= item.value;
    });
    return item ? (num / item.value).toFixed(digits).replace(rx, "$1") + item.symbol : "0";
  }

  export default NumberFormatter

// X-axis dimension keys (org-meta axis `x` values) whose ticks are measured in km.
export const KM_X_AXIS_KEYS = new Set(["odometer", "latestSOHPredictionOdometer"]);

// Axis-tick formatter for km dimensions: values >= 1000 are scaled to "Nk km"
// (e.g. 1000 -> "1k km", 240000 -> "240k km", 66500 -> "66.5k km"); values below
// 1000 stay as "N km".
export function formatKmAxisTick(val) {
  const n = Math.round(val);
  if (Math.abs(n) >= 1000) {
    const k = n / 1000;
    return `${Number.isInteger(k) ? k : parseFloat(k.toFixed(1))}k km`;
  }
  return `${n} km`;
}

// Truncates (never rounds up) to at most `decimals` places and drops trailing
// zeros: (916.657, 1) -> "916.6", (2.50, 2) -> "2.5", (3.004, 2) -> "3".
const truncateToDecimals = (value, decimals) => {
  const factor = 10 ** decimals;
  return `${Math.trunc(value * factor) / factor}`;
};

// Guards the decimal count against bad config (strings, negatives, nonsense)
// before it reaches 10 ** decimals, falling back to the caller's default.
const resolveDecimals = (decimals, fallback) => {
  // Not `Number(x) || fallback`: Number(null) and Number("") are both 0, which
  // would read a missing config value as "no decimals".
  if (decimals === null || decimals === undefined || decimals === "") return fallback;
  const n = Math.trunc(Number(decimals));
  return Number.isFinite(n) && n >= 0 && n <= 6 ? n : fallback;
};

export const DEFAULT_COMPACT_DISTANCE_DECIMALS = 0;
export const DEFAULT_COMPACT_ENERGY_DECIMALS = 1;

// KPI-tile distance formatter: collapses to "k"/"M" units so a fleet total stays
// readable (622567 -> "622k km", 2500000 -> "2M km"); below 1000 stays "N km".
// `decimals` applies to whichever unit is picked and defaults to whole numbers.
// Truncated, so the leading digits always match the raw reading.
// Distinct from formatKmAxisTick, which rounds and keeps one decimal at the "k"
// scale for axis ticks and table cells.
export function formatCompactDistanceKm(km, decimals) {
  const n = parseFloat(km);
  if (isNaN(n)) return null;
  const d = resolveDecimals(decimals, DEFAULT_COMPACT_DISTANCE_DECIMALS);
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);

  if (abs >= 1e6) return `${sign}${truncateToDecimals(abs / 1e6, d)}M km`;
  if (abs >= 1e3) return `${sign}${truncateToDecimals(abs / 1e3, d)}k km`;
  return `${sign}${Math.trunc(abs)} km`;
}

// KPI-tile energy formatter: input is kWh, stepped up to MWh at 1000 kWh and
// GWh at 1000000 kWh (916657 -> "916.6 MWh", 1250000 -> "1.2 GWh").
// `decimals` applies to whichever unit is picked and defaults to one place.
// Truncated, trailing zeros dropped.
export function formatCompactEnergyKWh(kWh, decimals) {
  const n = parseFloat(kWh);
  if (isNaN(n)) return null;
  const d = resolveDecimals(decimals, DEFAULT_COMPACT_ENERGY_DECIMALS);
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);

  if (abs >= 1e6) return `${sign}${truncateToDecimals(abs / 1e6, d)} GWh`;
  if (abs >= 1e3) return `${sign}${truncateToDecimals(abs / 1e3, d)} MWh`;
  return `${sign}${Math.trunc(abs)} kWh`;
}